class ProductForm {
    constructor(form) {
        this.form = form;
        this.quantityField = form.querySelector("[data-quantity-input]");
        this.quantityMinus = form.querySelector("[data-quantity-minus]");
        this.quantityPlus = form.querySelector("[data-quantity-plus]");
        this.addToCartBtn = form.querySelector("[data-add-to-cart]");

        this.init();
    }

    init() {
        this.setupQuantityButtons();
        this.setupFormSubmission();
        this.setupVariantSelectors();
    }

    setupQuantityButtons() {
        if (this.quantityMinus && this.quantityPlus && this.quantityField) {
            this.quantityMinus.addEventListener("click", (e) => {
                e.preventDefault();
                const currentValue = parseInt(this.quantityField.value) || 1;
                if (currentValue > 1) {
                    this.quantityField.value = currentValue - 1;
                }
            });

            this.quantityPlus.addEventListener("click", (e) => {
                e.preventDefault();
                const currentValue = parseInt(this.quantityField.value) || 1;
                this.quantityField.value = currentValue + 1;
            });
        }
    }

    setupFormSubmission() {
        if (this.form) {
            this.form.addEventListener("submit", (e) => {
                e.preventDefault();
                this.handleAddToCart();
            });
        }
    }

    async handleAddToCart() {
        const formData = new FormData(this.form);
        const btnText = this.addToCartBtn.querySelector(".btn__text");
        const btnLoading = this.addToCartBtn.querySelector(".btn__loading");

        this.setLoadingState(true, btnText, btnLoading);

        try {
            const response = await fetch("/cart/add.js", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    items: [
                        {
                            id: formData.get("id"),
                            quantity: parseInt(formData.get("quantity")),
                        },
                    ],
                }),
            });

            const data = await response.json();

            if (response.ok) {
                this.showSuccess();
                this.updateCartCount();
                document.dispatchEvent(new CustomEvent('cart:item-added', { detail: data }));
            } else {
                throw new Error(data.message || "Erreur lors de l'ajout au panier");
            }
        } catch (error) {
            console.error("Erreur lors de l'ajout au panier:", error);
            this.showError();
        } finally {
            this.setLoadingState(false, btnText, btnLoading);
        }
    }

    setLoadingState(loading, btnText, btnLoading) {
        if (loading) {
            btnText.style.display = "none";
            btnLoading.style.display = "inline";
            this.addToCartBtn.disabled = true;
        } else {
            btnText.style.display = "inline";
            btnLoading.style.display = "none";
            this.addToCartBtn.disabled = false;
        }
    }

    showSuccess() {
        if (window.toast) {
            window.toast.success("Produit ajouté au panier");
        }
    }

    showError() {
        if (window.toast) {
            window.toast.error("Erreur lors de l'ajout au panier");
        }
    }

    updateCartCount() {
        fetch("/cart.js")
            .then((response) => response.json())
            .then((cart) => {
                const cartCount = document.querySelector("[data-cart-count]");
                if (cartCount) {
                    cartCount.textContent = cart.item_count;

                    if (cart.item_count === 0) {
                        cartCount.classList.add("is-hidden");
                    } else {
                        cartCount.classList.remove("is-hidden");
                    }
                }
            })
            .catch((error) => console.error("Erreur lors de la mise à jour du compteur:", error));
    }

    setupVariantSelectors() {
        const variantSelectors = this.form.querySelectorAll(".variant-option-input");
        variantSelectors.forEach((selector) => {
            selector.addEventListener("change", () => {
                if (selector.checked) {
                    this.updateSelectedOptionLabel(selector);
                }
                this.updateVariant();
            });
        });
    }

    updateSelectedOptionLabel(input) {
        const optionName = input.dataset.optionName;
        const optionValue = input.dataset.optionValue;
        const selectedValueSpan = document.querySelector(`[data-option="${optionName}"]`);

        if (selectedValueSpan) {
            selectedValueSpan.textContent = optionValue;
        }
    }

    updateVariant() {
        const selectedOptions = this.getSelectedOptions();
        const matchingVariant = this.findMatchingVariant(selectedOptions);

        if (matchingVariant) {
            this.updateVariantId(matchingVariant.id);
            this.updatePrice(matchingVariant);
            this.updateAvailability(matchingVariant);
            this.updateAddToCartButton(matchingVariant);
            this.updateGallery(matchingVariant);
        }
    }

    getSelectedOptions() {
        const options = {};
        const variantInputs = this.form.querySelectorAll(".variant-option-input:checked");

        variantInputs.forEach((input) => {
            const optionName = input.dataset.optionName;
            const optionValue = input.dataset.optionValue;
            options[optionName] = optionValue;
        });

        return options;
    }

    findMatchingVariant(selectedOptions) {
        if (!window.productVariants) return null;

        return window.productVariants.find((variant) => {
            return variant.options.every((option, index) => {
                const optionName = window.productOptions[index];
                return selectedOptions[optionName] === option;
            });
        });
    }

    updateVariantId(variantId) {
        const variantIdInput = this.form.querySelector("[data-variant-id]");
        if (variantIdInput) {
            variantIdInput.value = variantId;
        }
    }

    updatePrice(variant) {
        const priceElements = document.querySelectorAll(".price");
        priceElements.forEach((priceElement) => {
            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
                priceElement.innerHTML = `
          <span class="price__sale">${this.formatMoney(variant.price)}</span>
          <span class="price__regular">${this.formatMoney(variant.compare_at_price)}</span>
        `;
            } else {
                priceElement.innerHTML = `<span class="price__regular">${this.formatMoney(variant.price)}</span>`;
            }
        });
    }

    updateAvailability(variant) {
        const availabilityElements = document.querySelectorAll(".availability-text");
        availabilityElements.forEach((element) => {
            if (variant.available) {
                element.textContent = "En stock";
                element.className = "availability-text availability-text--in-stock";
            } else {
                element.textContent = "Rupture de stock";
                element.className = "availability-text availability-text--out-of-stock";
            }
        });
    }

    updateAddToCartButton(variant) {
        if (this.addToCartBtn) {
            if (variant.available) {
                this.addToCartBtn.disabled = false;
                this.addToCartBtn.className = "btn btn--primary";
                const btnText = this.addToCartBtn.querySelector(".btn__text");
                if (btnText) {
                    btnText.textContent = "Ajouter au panier";
                }
            } else {
                this.addToCartBtn.disabled = true;
                this.addToCartBtn.className = "btn btn--primary btn--disabled";
                const btnText = this.addToCartBtn.querySelector(".btn__text");
                if (btnText) {
                    btnText.textContent = "Indisponible";
                }
            }
        }
    }

    updateGallery(variant) {
        if (window.productGallery && typeof window.productGallery.updateGalleryForVariant === "function") {
            requestAnimationFrame(() => {
                window.productGallery.updateGalleryForVariant(variant.id);
            });
        }
    }

    formatMoney(cents) {
        const amount = cents / 100;
        return new Intl.NumberFormat("fr-FR", {
            style: "currency",
            currency: "EUR",
        }).format(amount);
    }
}

document.addEventListener("DOMContentLoaded", () => {
    const productForms = document.querySelectorAll(".product-form:not([data-product-page-form])");
    productForms.forEach((form) => {
        new ProductForm(form);
    });
});